import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import axios from 'axios';
import Breadcrumb from '../Pages/Breadcrumb';
import Image from '../ui/Image';
import NewsLoading from '../loading/news-loading';
import { useLanguage } from '../../store/language-context';
import { useTranslation } from 'react-i18next';

const Blogs = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const pageNumber = searchParams.get("page") || 1;
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(pageNumber);
  const { URL } = useLanguage();
  const perPage = 6;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['allBlogs', currentPage, URL],
    queryFn: async () => {
      const response = await axios.get(`${URL}/blogs?page=${currentPage}&per_page=${perPage}`);
      if (response.status !== 200) throw new Error(t('custom.err'));
        return response.data;
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const news = data?.data?.data || [];
  const meta = data?.data?.meta || {};
  const lastPage = meta?.last_page || 1;

  const pageChangeFn = (pageNumber) => {
    if (pageNumber !== currentPage && pageNumber >= 1 && pageNumber <= lastPage) {
      setCurrentPage(pageNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(`/blogs?page=${pageNumber}`)
    }
  };

  const paginationNumbers = () => {
    const numbers = [];
    for (let i = 1; i <= lastPage; i++) {
      if (
        i <= 5 ||
        i === currentPage ||
        i === lastPage ||
        (i === currentPage - 1 && currentPage > 3) ||
        (i === currentPage + 1 && currentPage < lastPage)
      ) {
        numbers.push(i);
      }
    }

    const paginationWithDots = [];
    for (let i = 0; i < numbers.length; i++) {
      paginationWithDots.push(numbers[i]);
      if (i < numbers.length - 1 && numbers[i + 1] - numbers[i] > 1) {
        paginationWithDots.push("...");
      }
    }
    return paginationWithDots;
  };

  if (error) {
    return (
      <div className="container">
        <div className="latest-news-container text-center py-8">
          <p className="text-red-500">عذراً، حدث خطأ أثناء تحميل الأخبار</p>
          <button
            className="mt-2 bg-secondary-tw text-white px-4 py-2 rounded-md"
            onClick={refetch}
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb pages={[t("routes.all-blogs")]} />
      <main className="px-20 py-8 max-lg:px-4">
        <section className="space-y-8">
          <section className="grid grid-cols-3 gap-4 max-2xl:grid-cols-2 max-lg:grid-cols-1 max-lg:px-2">
            {isLoading ? (
              <NewsLoading />
            ) : news.length > 0 ? (
              news.map((item) => (
                <div key={item?.id || Math.random()} className="w-full h-full">
                  <Link to={`/blogs/${item?.id}`}>
                    <div className="relative w-full h-60">
                      <Image
                        src={item?.image}
                        alt={item?.title}
                        className="rounded-md object-cover w-full h-full"
                      />
                    </div>
                    <p className="text-base my-2 text-primary-tw font-semibold">
                      {item?.title}
                    </p>
                  </Link>
                </div>
              ))
            ) : (
              <p className="text-center">{t('custom.no-data')}</p>
            )}
          </section>

          <div className="flex flex-wrap gap-2 justify-center items-center">
            {/* Previous Button */}
            <button
              className="border-secondary-tw border-2 px-2 py-1 rounded-md hover:ring-1 hover:ring-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentPage === 1}
              onClick={() => pageChangeFn(currentPage - 1)}
            >
              <ArrowRight size={20} color="#e08b3d" />
            </button>

            {/* Page Numbers */}
            {paginationNumbers().map((num, index) => (
              <button
                key={index}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${num === currentPage
                  ? ' text-secondary-tw border-2 border-secondary-tw'
                  : num === '...'
                    ? 'text-gray-400 cursor-default'
                    : 'text-gray-600 hover:text-secondary-tw'
                  }`}
                onClick={() => num !== '...' && pageChangeFn(num)}
                disabled={num === '...'}
              >
                {num}
              </button>
            ))}

            {/* Next Button */}
            <button
              className="border-secondary-tw border-2 px-2 py-1 rounded-md hover:ring-1 hover:ring-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentPage === lastPage}
              onClick={() => pageChangeFn(currentPage + 1)}
            >
              <ArrowLeft size={20} color="#e08b3d" />
            </button>
          </div>
        </section>
      </main>
    </>
  );
};

export default React.memo(Blogs);